import { FC, useState, useEffect, useRef } from 'react';
import { Copy, Check, CheckCircle, X, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-python';

// Override Prism's default background
const customStyles = `
  .language-python {
    background: white !important;
  }
  .token.comment {
    color: #6A737D !important;
  }
  .token.keyword {
    color: #D73A49 !important;
  }
  .token.string {
    color: #032F62 !important;
  }
  .token.number {
    color: #005CC5 !important;
  }
  .token.function {
    color: #6F42C1 !important;
  }
`;

interface CodeBlockProps {
    code: string;
    language?: string;
    className?: string;
    onApply?: () => void;
    onReject?: () => void;
    showApplyButton?: boolean;
    showRejectButton?: boolean;
    isApplied?: boolean;
    isTyping?: boolean;
}

const PREVIEW_LINES = 8;

// Create style element once outside component
let styleElement: HTMLStyleElement | null = null;
if (typeof document !== 'undefined' && !styleElement) {
    styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);
}

const CodeBlock: FC<CodeBlockProps> = ({
    code,
    language = 'python',
    className,
    onApply,
    onReject,
    showApplyButton = false,
    showRejectButton = false,
    isApplied = false,
    isTyping = false
}) => {
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const codeRef = useRef<HTMLElement>(null);

    useEffect(() => {
        // Only highlight when not typing to avoid DOM conflicts
        if (!isTyping && codeRef.current && code) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                if (codeRef.current) {
                    Prism.highlightElement(codeRef.current);
                }
            });
        }
    }, [code, expanded, isTyping]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleApply = () => {
        if (onApply) {
            onApply();
        }
    };

    const handleReject = () => {
        if (onReject) {
            onReject();
        }
    };

    // Split code into lines for preview
    const codeLines = code.split('\n');
    const isLong = codeLines.length > PREVIEW_LINES;
    const previewCode = codeLines.slice(0, PREVIEW_LINES).join('\n');

    return (
        <div className={cn("relative rounded-md overflow-hidden border border-gray-200 bg-white", className)}>
            <div className="flex items-center justify-end px-1 border-b border-gray-200 bg-gray-100">
                <div className="flex items-center gap-1.5">
                    {showRejectButton && !isApplied && (
                        <button
                            onClick={handleReject}
                            className="px-1 py-0.5 rounded-md transition-colors"
                            title="Reject suggestion"
                        >
                            <X className="w-3 h-3 text-gray-500 hover:text-red-600" />
                        </button>
                    )}
                    {showApplyButton && !isApplied && (
                        <button
                            onClick={handleApply}
                            className="px-1 py-0.5 rounded-md transition-colors"
                            title="Apply code"
                        >
                            <CheckCircle className="w-3 h-3 text-blue-500 hover:text-blue-700" />
                        </button>
                    )}
                    {isApplied && (
                        <div className="px-1 py-0.5">
                            <Check className="w-3 h-3 text-green-500" />
                        </div>
                    )}
                    <button
                        onClick={handleCopy}
                        className="px-1 py-0.5 rounded-md transition-colors"
                        title="Copy code"
                    >
                        {copied ? (
                            <Check className="w-3 h-3 text-green-500" />
                        ) : (
                            <Copy className="w-3 h-3 text-gray-500 hover:text-gray-700" />
                        )}
                    </button>
                    {isLong && (
                        <button
                            onClick={() => setExpanded(e => !e)}
                            className="px-1 py-0.5 rounded-md transition-colors"
                            title={expanded ? 'Collapse' : 'Expand'}
                        >
                            <ChevronsUpDown className="w-3 h-3 text-gray-500 hover:text-gray-700" />
                        </button>
                    )}
                </div>
            </div>
            <div className="relative">
                <pre className="overflow-x-auto bg-white" style={!expanded && isLong ? { maxHeight: `${1.7 * PREVIEW_LINES}em`, overflow: 'hidden' } : {}}>
                    <code className={`language-${language} text-xs font-mono`} ref={codeRef}>
                        {expanded || !isLong ? code : previewCode}
                    </code>
                    {isTyping && (
                        <span className="inline-block w-0.5 h-3 bg-gray-600 animate-pulse ml-0.5" />
                    )}
                </pre>
                {!expanded && isLong && (
                    <div className="absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                )}
            </div>
        </div>
    );
};

export default CodeBlock; 